import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

export interface PolygonEvent {
  event: google.maps.MapMouseEvent;
  data: any; // Puoi specificare un tipo più preciso se conosci la struttura del dato
}

@Component({
  selector: 'agm-polygon',
  host: { '[attr.data-unique-id]': 'uniqueId' },
  template: `
    <ng-template #infoWindowTemplate>
      <div class="agm-info-window-content">
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
  standalone: false,
})
export class AgmPolygonComponent {
  uniqueId = `obj-${Math.random().toString(36).substr(2, 9)}`; // Genera un ID unico
  // Input per configurare il poligono
  @Input() paths?: any = []; // Supporta percorsi annidati o non annidati
  @Input() fillColor: string;
  @Input() data: any;
  @Input() fillOpacity: number;
  @Input() strokeColor: string;
  @Input() strokeOpacity: number;
  @Input() strokeWeight: number;
  @Input() clickable: boolean = false;
  @Input() editable: boolean = false;
  @Input() enableInfoWindow: boolean = false;

  // Output per gli eventi
  @Output() polyClick = new EventEmitter<PolygonEvent>();
  @Output() polyMouseOver = new EventEmitter<google.maps.MapMouseEvent>();
  @Output() polyMouseOut = new EventEmitter<google.maps.MapMouseEvent>();

  private polygon: google.maps.Polygon | null = null;
  private infoWindow!: google.maps.InfoWindow;

  @ViewChild('infoWindowTemplate', { static: true }) infoWindowTemplate!: TemplateRef<any>;
  @ViewChild('infoWindowTemplate', { static: true, read: ViewContainerRef })
  viewContainerRef!: ViewContainerRef;

  // questo getter per esporre `polygon`
  get googlePolygon(): google.maps.Polygon | null {
    return this.polygon;
  }

  initialize(map: google.maps.Map) {
    // Normalizza i percorsi (paths)
    const normalizedPaths = this.normalizePaths(this.paths);

    // Configurazione del poligono
    this.polygon = new google.maps.Polygon({
      paths: normalizedPaths,
      fillColor: this.fillColor,
      fillOpacity: this.fillOpacity,
      strokeColor: this.strokeColor,
      strokeOpacity: this.strokeOpacity,
      strokeWeight: this.strokeWeight,
      clickable: this.clickable,
      editable: this.editable,
      map,
    });

    // Imposta il dato personalizzato usando il metodo `set`
    if (this.data) {
      this.polygon.set('data', this.data);
    }

    // Configurazione dell'info window
    this.infoWindow = new google.maps.InfoWindow({
      content: this.renderDynamicContent(),
    });

    // Listener per il clic sul poligono
    if (this.clickable) {
      this.polygon.addListener('click', (event: google.maps.MapMouseEvent) => {
        this.polyClick.emit({
          event,
          data: this.polygon?.get('data'), // Recupera il dato personalizzato
        });
        if (this.enableInfoWindow) {
          this.infoWindow.setPosition(event.latLng); // Posiziona l'info window
          this.infoWindow.open(map, this.polygon); // Apre l'info window
        }
      });
    }

    // Listener per il mouseover sul poligono
    this.polygon.addListener('mouseover', (event: google.maps.MapMouseEvent) => {
      this.polyMouseOver.emit(event);
    });

    // Listener per il mouseout dal poligono
    this.polygon.addListener('mouseout', (event: google.maps.MapMouseEvent) => {
      this.polyMouseOut.emit(event);
    });
  }

  private renderDynamicContent(): HTMLElement {
    const element = document.createElement('div');
    const embeddedView = this.viewContainerRef.createEmbeddedView(this.infoWindowTemplate);

    // Rendi il contenuto visibile
    embeddedView.detectChanges();

    // Aggiungi il contenuto generato dinamicamente
    embeddedView.rootNodes.forEach((node) => element.appendChild(node));
    return element;
  }

  getPaths(): google.maps.LatLngLiteral[] {
    return this.paths ? this.paths : [];
  }

  openInfoWindow(): void {
    if (this.polygon) {
      this.infoWindow.open(this.polygon.getMap(), this.polygon); // Apri l'info window
    }
  }

  closeInfoWindow(): void {
    this.infoWindow.close(); // Chiudi l'info window
  }

  /**
   * Normalizza percorsi annidati o piatti in array di oggetti { lat, lng }.
   */
  private normalizePaths(paths: any[]): google.maps.LatLngLiteral[] {
    if (!paths || !Array.isArray(paths)) {
      return [];
    }

    // Se il primo livello è un array annidato, piattaforma i percorsi
    if (Array.isArray(paths[0])) {
      return paths[0].map((point: any) => this.validateLatLng(point));
    }

    // Altrimenti, usa i percorsi direttamente
    return paths.map((point: any) => this.validateLatLng(point));
  }

  /**
   * Convalida e converte un punto in formato { lat, lng }.
   */
  private validateLatLng(point: any): google.maps.LatLngLiteral {
    if (!point || typeof point.lat !== 'number' || typeof point.lng !== 'number') {
      return { lat: 0, lng: 0 }; // Ritorna un valore predefinito per evitare errori
    }
    return { lat: point.lat, lng: point.lng };
  }

  remove(): void {
    if (!this.polygon) {
      return;
    }

    this.polygon.setMap(null);
    this.polygon = null;
  }
}
